/**
 * Phase 0 · Infra-1 · P0.CLI.5
 *
 * Call permissions helper. Covers BOTH microphone and camera (mirroring the
 * two actually needed for a video call). Exposed under the legacy
 * `useMicPermission` name for the permissions rationale screen.
 *
 * Android: explicit pre-flight via PermissionsAndroid (we can distinguish
 *          granted / denied / blocked = NEVER_ASK_AGAIN and we persist the
 *          latest observation).
 *
 * iOS: piggyback on libraries already in the bundle to trigger the real
 *      native prompts. Audio.requestPermissionsAsync() (expo-av) handles the
 *      microphone and ImagePicker.requestCameraPermissionsAsync()
 *      (expo-image-picker) handles the camera. Both use the same
 *      NSCameraUsageDescription / NSMicrophoneUsageDescription strings, both
 *      register the app in iOS Settings, and both return an authoritative
 *      status. We deliberately avoid pulling in expo-camera just to ask —
 *      that would force a dev-client rebuild for no functional benefit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, PermissionsAndroid } from 'react-native';
import { Audio } from 'expo-av';
import * as ImagePicker from 'expo-image-picker';

export type PermissionStatus = 'granted' | 'denied' | 'blocked' | 'undetermined';

export interface CallPermissionState {
  status: PermissionStatus;
  loading: boolean;
  /** Request *both* mic and camera. Returns the combined status. */
  request: () => Promise<PermissionStatus>;
  /** Recheck without prompting. */
  refresh: () => Promise<PermissionStatus>;
}

async function checkAndroidBoth(): Promise<PermissionStatus> {
  const [micGranted, camGranted] = await Promise.all([
    PermissionsAndroid.check(PermissionsAndroid.PERMISSIONS.RECORD_AUDIO),
    PermissionsAndroid.check(PermissionsAndroid.PERMISSIONS.CAMERA),
  ]);
  if (micGranted && camGranted) return 'granted';
  // We can't distinguish "never asked" from "denied" without the last request
  // result, so we default to `undetermined` when nothing is granted — the
  // consumer will call `request()` which gives us authoritative state.
  return 'undetermined';
}

async function requestAndroidBoth(): Promise<PermissionStatus> {
  const results = await PermissionsAndroid.requestMultiple([
    PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
    PermissionsAndroid.PERMISSIONS.CAMERA,
  ]);
  const values = Object.values(results);
  if (values.every((v) => v === PermissionsAndroid.RESULTS.GRANTED)) {
    return 'granted';
  }
  if (values.some((v) => v === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN)) {
    return 'blocked';
  }
  return 'denied';
}

// Combine an iOS expo PermissionResponse pair into our 4-state model.
// 'granted'      → both granted
// 'blocked'      → at least one denied AND not requestable again
// 'denied'       → at least one denied (still requestable)
// 'undetermined' → at least one still pending (we never expect this after a request)
function combineIosStatuses(
  mic: { status: string; canAskAgain?: boolean },
  cam: { status: string; canAskAgain?: boolean }
): PermissionStatus {
  if (mic.status === 'granted' && cam.status === 'granted') return 'granted';
  const anyDenied = mic.status === 'denied' || cam.status === 'denied';
  const anyUnaskable =
    (mic.status === 'denied' && mic.canAskAgain === false) ||
    (cam.status === 'denied' && cam.canAskAgain === false);
  if (anyUnaskable) return 'blocked';
  if (anyDenied) return 'denied';
  return 'undetermined';
}

async function checkIosBoth(): Promise<PermissionStatus> {
  try {
    const [mic, cam] = await Promise.all([
      Audio.getPermissionsAsync(),
      ImagePicker.getCameraPermissionsAsync(),
    ]);
    return combineIosStatuses(mic, cam);
  } catch {
    return 'undetermined';
  }
}

async function requestIosBoth(): Promise<PermissionStatus> {
  // Mic first, then camera — two prompts, easy for the user to follow.
  const mic = await Audio.requestPermissionsAsync();
  const cam = await ImagePicker.requestCameraPermissionsAsync();
  return combineIosStatuses(mic, cam);
}

/**
 * Standalone permission check — non-hook version of `useMicPermission`'s
 * `refresh()`. Used by call-entry buttons (e.g. perfil's "Join room") so we
 * can route the user **directly** into the lobby when both mic and camera
 * are already granted, instead of flashing the rationale screen for a
 * frame while the hook resolves.
 *
 * On web we always return 'undetermined' — the rationale screen has its
 * own copy that explains the browser prompt, and we can't reliably probe
 * the Permissions API across all browsers without surface area we don't
 * yet need.
 */
export async function checkCallPermissions(): Promise<PermissionStatus> {
  if (Platform.OS === 'android') return checkAndroidBoth();
  if (Platform.OS === 'ios') return checkIosBoth();
  return 'undetermined';
}

export function useMicPermission(): CallPermissionState {
  const [status, setStatus] = useState<PermissionStatus>('undetermined');
  const [loading, setLoading] = useState(true);
  const lastObservedRef = useRef<PermissionStatus>('undetermined');

  const refresh = useCallback(async (): Promise<PermissionStatus> => {
    setLoading(true);
    try {
      if (Platform.OS === 'android') {
        const next = await checkAndroidBoth();
        // Preserve a previously observed terminal state (denied/blocked/granted).
        const preserved =
          lastObservedRef.current === 'blocked' ||
          lastObservedRef.current === 'denied'
            ? lastObservedRef.current
            : next;
        const resolved = next === 'granted' ? 'granted' : preserved;
        setStatus(resolved);
        return resolved;
      }
      const next = await checkIosBoth();
      lastObservedRef.current = next;
      setStatus(next);
      return next;
    } finally {
      setLoading(false);
    }
  }, []);

  const request = useCallback(async (): Promise<PermissionStatus> => {
    setLoading(true);
    try {
      if (Platform.OS === 'android') {
        const next = await requestAndroidBoth();
        lastObservedRef.current = next;
        setStatus(next);
        return next;
      }
      const next = await requestIosBoth();
      lastObservedRef.current = next;
      setStatus(next);
      return next;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { status, loading, request, refresh };
}
